import { useLayoutEffect, useState } from "react";

/**
 * Returns a scale factor (0..1] so a fixed-width canvas fits within its
 * container without horizontal overflow. Uses ResizeObserver on the
 * container element (RO delivers an initial observation on observe, so no
 * separate first measurement is needed).
 *
 * Attachment retries on an animation frame until the ref is populated:
 * reading `containerRef.current` during render is NOT a reliable trigger —
 * on a mount where data is already cached (no loading-gate re-render), the
 * ref attaches after the first render and nothing re-runs the effect, so
 * the canvas stayed at zoom 1 and overflowed until any state change
 * re-rendered the component.
 *
 * CSS `zoom` (not transform) keeps document flow, container queries, and
 * scroll height consistent while scaling the rendering. Editor chrome
 * inside the canvas counter-zooms via --canvas-unzoom so toolbars and
 * pills stay at native size.
 */
export function useCanvasZoom(
  containerRef: React.RefObject<HTMLElement | null>,
  viewportWidth: number,
): number {
  const [availWidth, setAvailWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    let observer: ResizeObserver | null = null;
    let raf = 0;

    const attach = () => {
      const el = containerRef.current;
      if (!el) {
        raf = requestAnimationFrame(attach);
        return;
      }
      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry) setAvailWidth(entry.contentRect.width);
      });
      observer.observe(el);
    };
    attach();

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
    };
  }, [containerRef]);

  if (availWidth == null) return 1;
  return Math.min(1, availWidth / viewportWidth);
}
